import React, { FormEvent } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  AbstractControl,
  FieldArray,
  FieldControl,
  FieldGroup,
  FormArray,
  FormBuilder,
  Validators
} from 'react-reactive-form';
import { AppState } from '../../../../store/store.types';
import { NumberInput, TextInput } from '../../../../components/forms/Input';
import { SelectedOption, StatusType } from '../../../../api/api.types';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import {
  itemEditRoles,
  itemGroupTypeOptions,
  ItemModel,
  ItemRequest,
  Locale,
  localeOptions,
  TranslationModel
} from '../../../../api/core/core.types';
import Breadcrumbs from '../../../../components/breadcrumbs/Breadcrumbs';
import Select from '../../../../components/forms/Select';
import { addNewItem, deleteItem, editItem, getItem } from '../../actions/item.actions';
import { ItemActionTypes } from '../../types/item.types';
import StatusLabel from '../../../../components/forms/StatusLabel';
import Checkbox from '../../../../components/forms/Checkbox';
import { AdminApi } from '../../../../api/api.apiclient';
import { UserModel } from '../../../../api/authentication/authentication.types';
import { userCanAccessComponent } from '../../../../helpers/authentication.helper';
import ExpandButton from '../../../../components/forms/ExpandButton';
import { getLanguageName } from '../../../../helpers/locale.helper';
import { InputTextArea } from '../../../../components/forms/TextArea';
import SectionTitle from '../../../../components/forms/SectionTitle';
import ImageUpload from '../../../../components/forms/ImageUpload';
import placeHolder from '../../../../assets/img/nocar.svg';
import { WithTranslation, withTranslation } from 'react-i18next';
import { ClassifierModel, ClassifierTypes } from '../../../../api/admin/admin.types';

interface ItemLocationProps {
  id: string;
}

interface ItemDetailsProps extends RouteComponentProps<ItemLocationProps>, WithTranslation {
  selectedItem: ItemModel | null;
  getSelectedItem: (id: number) => void;
  editItem: (id: number, request: ItemRequest) => void;
  addNewItem: (request: ItemRequest) => void;
  deleteItem: (id: number) => void;
  isNewCode?: boolean;
  postSuccess: boolean | null;
  lastFetch: number | null;
  user: UserModel | null;
}

interface ItemDetailsState {
  isModalOpen: boolean;
  isEdit: boolean;
  otaCodes: ClassifierModel[];
  showNameTranslations: boolean;
  showDescriptionTranslations: boolean;
}

class ItemDetails extends React.Component<ItemDetailsProps, ItemDetailsState> {

  readonly state = {
    isModalOpen: false,
    isEdit: false,
    otaCodes: [] as ClassifierModel[],
    showNameTranslations: false,
    showDescriptionTranslations: false
  };

  form = FormBuilder.group({
    otaCode: [ null, Validators.required ],
    itemGroupType: [ null, Validators.required ],
    maxQuantity: [ null, Validators.min(0) ],
    name: [ null, Validators.required ],
    description: [ null ],
    supplierReferenceCode: [ null ],
    image: [ null ],
    active: [ true ],
    nameTranslations: FormBuilder.array([]),
    descriptionTranslations: FormBuilder.array([])
  });

  componentDidMount() {
    AdminApi.Classifiers.listAllClassifiersByType(ClassifierTypes.ITEM_OTA_CODE).then((response) => {
      this.setState({ otaCodes: response });
    });

    const { id } = this.props.match.params;
    if (id) {
      this.props.getSelectedItem(Number(id));
    }


    if (this.props.isNewCode) {
      this.createTranslationForm(this.form.controls.nameTranslations as FormArray, 'nameTranslations');
      this.createTranslationForm(this.form.controls.descriptionTranslations as FormArray, 'descriptionTranslations');

    } else if (this.props.selectedItem) {
      this.assignTranslationToForm(this.props.selectedItem.nameTranslations, 'nameTranslations');
      this.assignTranslationToForm(this.props.selectedItem.descriptionTranslations, 'descriptionTranslations');
    }

    this.form.controls.otaCode.patchValue(this.getOtaCode());
  }

  componentDidUpdate(prevProps: ItemDetailsProps) {
    if ((this.props.isNewCode) && this.props.postSuccess && this.props.selectedItem) {
      this.props.history.push(`/setup/items/${this.props.selectedItem.id}/view`);
    }
    if (this.props.selectedItem && (!prevProps.selectedItem || this.props.selectedItem !== prevProps.selectedItem)) {
      this.updateFormValue();

      this.assignTranslationToForm(this.props.selectedItem.nameTranslations, 'nameTranslations');
      this.assignTranslationToForm(this.props.selectedItem.descriptionTranslations, 'descriptionTranslations');
    }
  }

  updateFormValue = () => {
    if (!this.props.selectedItem) {
      return;
    }
    this.form.patchValue({
      otaCode: this.getOtaCode(),
      itemGroupType: this.props.selectedItem.itemGroupType,
      maxQuantity: this.props.selectedItem.maxQuantity,
      name: this.props.selectedItem.name,
      image: this.props.selectedItem.image.original,
      description: this.props.selectedItem.description,
      supplierReferenceCode: this.props.selectedItem.supplierReferenceCode,
      active: this.props.selectedItem.statusType === StatusType.ACTIVE
    });
  };

  assignTranslationToForm = (translations: TranslationModel[], name: string) => {
    this.form.controls[ name ] = FormBuilder.array([]);
    const form = this.form.controls[ name ] as FormArray;

    translations.forEach((translation) => {
      form.push(FormBuilder.group({
        language: [ translation.language ],
        code: [ `${name} ${translation.language}` ],
        translation: [ translation.value ]
      }));
    });
  };

  createTranslationForm = (form: FormArray, type: string) => {
    localeOptions.forEach((locale: { value: Locale; label: string }) => {

      form.push(FormBuilder.group({
        language: [ locale.value ],
        code: [ `${type} ${locale.value}` ],
        translation: [ '' ]
      }));
    });
  };

  async onImageUpload(image: File) {
    const base64: any = await this.getBase64(image);
    this.form.patchValue({ image: base64.split(',')[ 1 ] });
  }

  // eslint-disable-next-line class-methods-use-this
  getBase64(image: File) {
    return new Promise(((resolve) => {
      const fileReader: FileReader = new FileReader();

      fileReader.onloadend = () => {
        resolve(fileReader.result);
      };
      fileReader.readAsDataURL(image);
    }));
  }

  getOtaCode = () => {
    if (this.props.selectedItem) {
      const otaCode = this.props.selectedItem.otaCode;
      const otaCodeClassifier = this.state.otaCodes.find((select) => select.code === otaCode);
      if (otaCodeClassifier) {
        return { value: otaCodeClassifier.code, label: otaCodeClassifier.stringValue };
      }
      return { value: otaCode, label: otaCode };
    }

    return { value: '', label: 'Choose OTA code' };
  };

  onDeleteConfirm = () => {
    const { id } = this.props.match.params;
    this.props.deleteItem(Number(id));
    this.props.history.push('/setup/items');
  };

  patchField = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option.value });
  };

  handleChangeValue = (option: SelectedOption, field: string) => {
    this.form.patchValue({ [ field ]: option });
  };

  onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const form = this.form.controls;
    this.form.markAsSubmitted();
    if (this.form.invalid) {
      return;
    }

    const nameTranslations: TranslationModel[] = this.getTranslationsArray(this.form.controls.nameTranslations as FormArray);
    const descriptionTranslations: TranslationModel[] = this.getTranslationsArray(this.form.controls.descriptionTranslations as FormArray);
    let base64Image;
    if (form.image.value && !form.image.value.includes('https://')) base64Image = form.image.value;
    const request: ItemRequest = {
      otaCode: form.otaCode.value.value,
      itemGroupType: form.itemGroupType.value,
      maxQuantity: form.maxQuantity.value,
      name: form.name.value,
      base64Image: base64Image,
      description: form.description.value,
      supplierReferenceCode: form.supplierReferenceCode.value,
      statusType: form.active.value ? StatusType.ACTIVE : StatusType.INACTIVE,
      nameTranslations,
      descriptionTranslations
    };

    if (this.props.selectedItem) {
      const { id } = this.props.match.params;

      this.props.editItem(Number(id), request);
      this.setState({ isEdit: false });

    } else {

      this.props.addNewItem(request);
    }
  };


  getTranslationsArray = (form: FormArray) => {
    const translations: TranslationModel[] = [];

    form.value.forEach((v: { language: Locale; translation: string; code: string }) => {
      const translation: TranslationModel = {
        language: v.language,
        value: v.translation
      };

      translations.push(translation);
    });

    return translations;
  };

  onCancel = () => {
    if (this.props.isNewCode) {
      this.props.history.push('/setup/items/');
    } else {
      this.updateFormValue();
      this.setState({ isEdit: false });
    }
  };

  onEdit = () => {
    this.setState({ isEdit: true });
    this.form.controls.otaCode.patchValue(this.getOtaCode());
  };

  onNameTranslationsClick = () => {
    this.setState({ showNameTranslations: !this.state.showNameTranslations });
  };

  onDescriptionTranslationsClick = () => {
    this.setState({ showDescriptionTranslations: !this.state.showDescriptionTranslations });
  };

  render() {
    if (!this.props.isNewCode && !this.props.selectedItem) {
      return null;
    }

    const showForm = this.state.isEdit || this.props.isNewCode;

    const { t } = this.props;

    return (
        <main>
          <Breadcrumbs
            items={
              [
                { path: '/setup', label: t('m1.setup') },
                { path: '/setup/items', label: t('m2.items') },
                {
                  path: null,
                  label: this.props.isNewCode ? 'New id' : this.props.selectedItem && String(this.props.selectedItem.id) || ''
                }
              ]
            }
          />
          <div className="page-content">
            <div className="row justify-content-end">
              <div className="col-12 d-flex align-items-center justify-content-between">
                <h1 className="mb-0">{this.props.isNewCode ? t('page_title.add_new_item') :
                  t('page_title.item_details')}</h1>
                {!!this.props.selectedItem && !showForm && this.props.user && userCanAccessComponent(this.props.user, itemEditRoles)
                  && (
                    <div>
                      <button type="button" className="btn btn-danger mr-2"
                              onClick={() => this.setState({ isModalOpen: true })}>{
                        t('b.delete_Generic delete button')}
                      </button>
                      <button type="button" className="btn btn-primary edit-button" onClick={() => this.onEdit()}>
                        {(t('b.edit_Generic edit button')).toUpperCase()}
                      </button>
                    </div>
                  )}
              </div>
            </div>
            <FieldGroup
              strict={false}
              control={this.form}
              render={() => (
                <form className="form-horizontal" onSubmit={(e) => this.onSubmit(e)}>
                  <hr/>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item.name')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedItem && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.name.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="name"
                            render={TextInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="row align-items-center justify-content-center mb-3 col-8">
                    <ExpandButton className="btn btn-primary edit-button" text="translations"
                                  onClick={this.onNameTranslationsClick}></ExpandButton>
                  </div>
                  {this.state.showNameTranslations && (
                    <>
                      <FieldArray
                        strict={false}
                        name="nameTranslations"
                        render={({ controls }: any) => controls.map((control: AbstractControl) => (
                          <FieldGroup
                            strict={false}
                            key={control.value.code}
                            control={control}
                            render={() => (
                              <div className="form-group d-flex">
                                <div className="col-4 d-flex align-items-center justify-content-end">
                                  <label>{getLanguageName(control.value.language)}</label>
                                </div>
                                {this.props.selectedItem && !showForm && (
                                  <div className="form-control-static">
                                    {control.value.translation}
                                  </div>
                                )}
                                {showForm && (
                                  <div className="col-2 d-flex align-items-center justify-content-start edit-form-input"
                                       key={control.value.code}>
                                    <FieldControl
                                      strict={false}
                                      name="translation"
                                      key={control.value.code}
                                      render={(props) => <TextInput {...props} id={control.value.code}></TextInput>}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          />
                        ))}
                      />
                      <hr/>
                    </>
                  )}
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item.ota_code')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedItem && !showForm
                        && (
                          <div className="form-control-static">
                            {this.getOtaCode().label}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="otaCode"
                            strict={false}
                            render={(props) => (
                              <Select
                                value={this.form.controls.otaCode.value}
                                disableClear
                                className="mr-1"
                                placeholder="Choose ota code"
                                options={this.state.otaCodes.map((d) => ({
                                  label: `${d.stringValue} ${d.name}`,
                                  value: d.code
                                }))}
                                disableMinWidth
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="ota code"
                                onChange={(value) => this.handleChangeValue(value as SelectedOption, 'otaCode')}
                              />
                            )}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item.item_type')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedItem && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.itemGroupType.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            strict={false}
                            name="itemGroupType"
                            render={(props) => (
                              <Select
                                value={itemGroupTypeOptions.find((select) => select.value === this.form.controls.itemGroupType.value)}
                                disableClear
                                className="mr-1"
                                placeholder="Choose item type"
                                disableMinWidth
                                options={itemGroupTypeOptions}
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                label="item type"
                                onChange={(value) => this.patchField(value as SelectedOption, 'itemGroupType')}
                              />
                            )}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.item.max_quantity')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedItem && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.maxQuantity.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="maxQuantity"
                            render={NumberInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-start justify-content-end">
                      <label>{t('l.item.description')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-start justify-content-start edit-form-input">
                      {
                        this.props.selectedItem && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.description.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="description"
                            render={InputTextArea}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="row align-items-center justify-content-center mb-3 col-8">
                    <ExpandButton className="btn btn-primary edit-button" text="translations"
                                  onClick={this.onDescriptionTranslationsClick}></ExpandButton>
                  </div>
                  {this.state.showDescriptionTranslations && (
                    <>
                      <FieldArray
                        strict={false}
                        name="descriptionTranslations"
                        render={({ controls }: any) => controls.map((control: AbstractControl) => (
                          <FieldGroup
                            strict={false}
                            key={control.value.code}
                            control={control}
                            render={() => (
                              <div className="form-group row">
                                <div className="col-4 d-flex align-items-center justify-content-end">
                                  <label>{getLanguageName(control.value.language)}: </label>
                                </div>
                                {this.props.selectedItem && !showForm && (
                                  <div className="form-control-static">
                                    {control.value.translation}
                                  </div>
                                )}
                                {showForm && (
                                  <div className="col-2 d-flex align-items-center justify-content-start"
                                       key={control.value.code}>
                                    <FieldControl
                                      strict={false}
                                      name="translation"
                                      key={control.value.code}
                                      render={(props) => <InputTextArea {...props}
                                                                        id={control.value.code}></InputTextArea>}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          />
                        ))}
                      />
                      <hr/>
                    </>
                  )}
                  <div className="form-group d-flex">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      <label>{t('l.item.supplier_ref')}:</label>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-start edit-form-input">
                      {
                        this.props.selectedItem && !showForm
                        && (
                          <div className="form-control-static">
                            {this.form.controls.supplierReferenceCode.value}
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="supplierReferenceCode"
                            render={TextInput}
                          />
                        )
                      }
                    </div>
                  </div>
                  <div className="form-group row">
                    <div className="col-4 d-flex align-items-center justify-content-end">
                      {showForm && (
                        <label className="mandatory-field">* </label>
                      )}
                      <label>{t('l.item.status')}:</label>
                    </div>
                    <div className="col-6 d-flex align-items-center">
                      {
                        this.props.selectedItem && !showForm
                        && (
                          <div className="form-control-static">
                            <StatusLabel status={this.props.selectedItem.statusType}></StatusLabel>
                          </div>
                        )
                      }
                      {
                        showForm
                        && (
                          <FieldControl
                            name="active"
                            render={(props) => <Checkbox {...props} id="statusField" className="mt-1">Active</Checkbox>}
                          />
                        )
                      }
                    </div>
                  </div>

                  <SectionTitle
                    title={t('section_title.item.image').toUpperCase()}
                  />

                  {this.props.selectedItem && !showForm && (
                    <div className="d-flex">
                      {this.form.controls.image && (
                        <div className="col-5 d-flex align-items-center justify-content-end">
                          <div className="col-6"></div>
                          <img className="item-image" alt="itemImage"
                               src={this.form.controls.image.value ? `${this.form.controls.image.value}?${new Date()}` : placeHolder}/>
                        </div>
                      )}
                      <div className="col-2">
                        <div className="col-2">
                          {this.props.selectedItem?.image.tiny && (
                            <div className="d-flex row info-container">
                              <a target="_blank" rel="noreferrer" href={this.props.selectedItem?.image.tiny}>Tiny</a>
                            </div>
                          )}
                          {this.props.selectedItem?.image.small && (
                            <div className="d-flex row info-container">
                              <a target="_blank" rel="noreferrer" href={this.props.selectedItem?.image.small}>Small</a>
                            </div>
                          )}
                          {this.props.selectedItem?.image.medium && (
                            <div className="d-flex row info-container">
                              <a target="_blank" rel="noreferrer"
                                 href={this.props.selectedItem?.image.medium}>Medium</a>
                            </div>
                          )}
                          {this.props.selectedItem?.image.large && (
                            <div className="d-flex row info-container">
                              <a target="_blank" rel="noreferrer" href={this.props.selectedItem?.image.large}>Large</a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {
                    showForm
                    && (
                      <div className="form-group row">
                        <div className="col-8 d-flex align-items-center justify-content-center">
                          <ImageUpload
                            image={this.form.controls.image.value}
                            isEdit
                            onImageSelect={(file: File) => this.onImageUpload(file)}
                          />
                        </div>
                      </div>)}

                  {showForm && (
                    <div className="row">
                      <div className="col-12 text-right">
                        <button type="button" className="btn btn-outline-secondary btn-lg mr-2"
                                onClick={this.onCancel}>
                          {t('b.cancel_Generic cancel button translation').toUpperCase()}
                        </button>
                        <button type="submit" className="btn btn-primary btn-lg" onClick={this.onSubmit}>
                          {t('b.save_Generic save button translation').toUpperCase()}
                        </button>
                      </div>
                    </div>

                  )}

                </form>
              )}

            />
          </div>

          <ConfirmationModal
            title="Confirm item removal"
            content="Are you sure you want to remove this item?"
            isOpen={this.state.isModalOpen}
            toggle={() => this.setState({ isModalOpen: false })}
            onConfirm={() => this.onDeleteConfirm()}
            onCancel={() => this.setState({ isModalOpen: false })}
          />
        </main>
    );
  }
}

const mapStateToProps = (state: AppState) => ({
  selectedItem: state.setup.items.selectedItem,
  postSuccess: state.setup.items.postSuccess,
  lastFetch: state.setup.items.lastFetch,
  user: state.authentication.user
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<{}, {}, ItemActionTypes>
) => ({
  getSelectedItem: (id: number) => dispatch(getItem(id)),
  editItem: (id: number, request: ItemRequest) => dispatch(editItem(id, request)),
  addNewItem: (request: ItemRequest) => dispatch(addNewItem(request)),
  deleteItem: (id: number) => dispatch(deleteItem(id))
});
export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(ItemDetails));
